import { RefObject, useEffect, useRef } from "react";
import Badge from "../../../../../../ui/Badge/Badge";
import TextInput from "../../../../../../ui/TextInput/TextInput";
import styles from "./Title.module.scss";
import { useMergeRefs } from "@floating-ui/react";

export default function Title({
  index,
  value,
  titleRef,
  onChange,
  headingLevel,
  appendSection,
  deleteSection,
  focusNext,
  focusPrevious,
  disabled,
  titleDisabled,
}: {
  index: number;
  value?: string;
  titleRef?:
    | RefObject<HTMLInputElement | null>
    | ((ref: HTMLInputElement | null) => void);
  onChange: (value: string) => void;
  headingLevel: number;
  appendSection?: () => void;
  deleteSection?: () => void;
  focusNext?: () => void;
  focusPrevious?: () => void;
  disabled?: boolean;
  titleDisabled?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const previousValue = useRef<string | undefined>(value);
  const cursor = useRef<number | null>(null);
  const changeFromInputRef = useRef(false);
  const refs = useMergeRefs([ref, titleRef]);

  useEffect(() => {
    const input = ref.current;

    if (!changeFromInputRef.current && cursor.current && input) {
      let newCursor = cursor.current;

      if (value && previousValue.current) {
        if (value.length < previousValue.current.length) {
          const indexOfChange = [...value].findIndex(
            (el, index) => el !== previousValue.current?.[index],
          );

          if (indexOfChange < newCursor) {
            newCursor -= previousValue.current.length - value.length;
          }
        } else {
          const indexOfChange = [...previousValue.current].findIndex(
            (el, index) => el !== value?.[index],
          );

          if (indexOfChange <= newCursor) {
            newCursor += value.length - previousValue.current.length;
          }
        }
      }

      input.setSelectionRange(newCursor, newCursor);
      cursor.current = newCursor;
    }

    changeFromInputRef.current = false;
    previousValue.current = value;
  }, [cursor, value]);

  return (
    <div className={styles.titleRow}>
      <Badge variant="badge" showDot={false} className={styles.badge}>
        {titleDisabled ? "-" : `H${headingLevel}`}
      </Badge>
      {titleDisabled ? (
        <span className={styles.titleDisabled}>
          {index === 0 ? "Introduction (No title)" : "No title"}
        </span>
      ) : (
        <TextInput
          unstyled
          autosize
          ref={refs}
          disabled={disabled}
          value={value}
          placeholder="Write section title"
          className={styles.title}
          onChange={(event) => {
            changeFromInputRef.current = true;
            onChange(event.target.value);
            cursor.current = event.target.selectionStart;
          }}
          onFocus={(event) => {
            cursor.current = event.target.selectionStart;
          }}
          onBlur={() => {
            cursor.current = null;
          }}
          onKeyDown={(event) => {
            cursor.current = ref.current?.selectionStart ?? null;

            if (event.key === "Enter") {
              event.preventDefault();

              appendSection?.();
            }

            if (event.key === "Backspace" && value === "") {
              event.preventDefault();

              deleteSection?.();
            }

            if (event.key === "ArrowDown") {
              focusNext?.();
            }

            if (event.key === "ArrowUp") {
              focusPrevious?.();
            }
          }}
        />
      )}
    </div>
  );
}
